import { useCallback, useEffect, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import type { PedidosLayoutOutletContext } from '@/layouts/PedidosLayout'
import { SaleQuestionnaireSection, PedidoStatusControl, PedidoStatusHistorySection } from '@/components/Pedidos'
import { formatCurrency } from '@/lib/campaign-dashboard'
import { fetchCampaignSaleDetail } from '@/lib/campaign-reports'
import { canChangePedidoStatus } from '@/lib/pedidos-permissions'
import { ConversionPageLink } from '@/components/Tracking'
import type { CampaignSaleDetailPayload } from '@/types/campaign-report'
import styles from './PedidoDetalhePage.module.css'

function formatDetailDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function fmtNum(n: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

function display(v: string | null | undefined): string {
  if (v === null || v === undefined || v.trim() === '') return '—'
  return v
}

function renderTouchLabel(value: string | null | undefined): string {
  if (!value || value.trim() === '') return '—'
  return value
}

export function PedidoDetalhePage() {
  const { paymentId: paymentIdParam } = useParams<{ paymentId: string }>()
  const paymentId = Number.parseInt(paymentIdParam ?? '', 10)
  const { token, guard, isCollaborator } = useAuth()
  const { institutionId } = useInstitution()
  const { campaignId, campaignsLoading, hasCampaigns } =
    useOutletContext<PedidosLayoutOutletContext>()

  const [data, setData] = useState<CampaignSaleDetailPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    if (campaignId == null || !Number.isFinite(paymentId) || paymentId < 1) {
      setData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchCampaignSaleDetail(
        campaignId,
        paymentId,
        token ?? undefined
      )
      setData(res)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Não foi possível carregar o pedido.'
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [campaignId, paymentId, token])

  useEffect(() => {
    void load()
  }, [load])

  const handleCopyPix = async () => {
    const text = data?.pix.copy_paste ?? ''
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  if (campaignsLoading || campaignId == null) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        Carregando…
      </div>
    )
  }

  if (!hasCampaigns) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:cart-outline" className={styles.errorIcon} />
        Nenhuma campanha disponível.
      </div>
    )
  }

  if (!Number.isFinite(paymentId) || paymentId < 1) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        Pedido inválido.
        <Link to="/pedidos/lista" className={styles.backLink}>
          Voltar à lista
        </Link>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        Carregando pedido…
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        {error ?? 'Pedido não encontrado.'}
        <button type="button" className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
        <Link to="/pedidos/lista" className={styles.backLink}>
          Voltar à lista
        </Link>
      </div>
    )
  }

  const {
    payment,
    donor,
    product,
    summary,
    pix,
    lucky_numbers,
    client_context,
    tracking,
    delivery,
    questionnaire,
    confirmation,
  } = data
  const questionnaireAnswers = questionnaire?.answers ?? []
  const paidAt = payment.paid_at ?? payment.created_at
  const donorName = donor.name ?? '—'
  const confirmationSource = confirmation?.source ?? null
  const trackingEvents = tracking.events ?? []
  const firstTouch = tracking.first_touch
  const lastTouch = tracking.last_touch

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <Link to="/pedidos/lista" className={styles.backLink}>
          <Icon icon="mdi:arrow-left" />
          Voltar para lista
        </Link>
        <nav className={styles.breadcrumb} aria-label="Navegação">
          <Link to="/home">Início</Link>
          <span className={styles.breadcrumbSep}>/</span>
          <Link to="/pedidos/lista">Pedidos</Link>
          <span className={styles.breadcrumbSep}>/</span>
          <span>Pedido #{payment.id}</span>
        </nav>
      </div>

      <section className={styles.card}>
        <div className={styles.headerCard}>
          <div className={styles.headerIcon}>
            <div className={styles.iconCircle} aria-hidden>
              <Icon icon="mdi:package-variant" />
            </div>
            <div>
              <h1 className={styles.headerTitle}>Pedido de {donorName}</h1>
              <p className={styles.headerMeta}>
                ID do Pedido: #{payment.id} &nbsp;|&nbsp; Data:{' '}
                {formatDetailDateTime(paidAt)}
              </p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <PedidoStatusControl
              campaignId={campaignId}
              paymentId={payment.id}
              currentStatus={payment.status}
              allowedTransitions={data.allowed_status_transitions}
              confirmationSource={confirmationSource}
              isManualApproval={confirmationSource === 'manual'}
              statusChanges={data.status_changes}
              showHistoryInMenu={false}
              canChangeStatus={canChangePedidoStatus(
                guard,
                isCollaborator,
                institutionId
              )}
              token={token}
              onStatusChanged={setData}
            />
          </div>
        </div>

        {confirmation?.manual ? (
          <div className={styles.manualApprovalInfo}>
            <p>
              <strong>Aprovado por:</strong>{' '}
              {confirmation.manual.approved_by_name}
            </p>
            <p>
              <strong>Em:</strong>{' '}
              {formatDetailDateTime(confirmation.manual.approved_at)}
            </p>
            <p>
              <strong>Motivo:</strong> {confirmation.manual.reason}
            </p>
          </div>
        ) : null}
      </section>

      <section className={styles.card}>
        <PedidoStatusHistorySection entries={data.status_changes} />
      </section>

      <section className={styles.card}>
        <div className={styles.productRow}>
          <div className={styles.productThumb}>
            {product.image_url ? (
              <img src={product.image_url} alt="" draggable={false} />
            ) : (
              <div className={styles.productThumbPlaceholder}>
                <Icon icon="mdi:image-off-outline" />
              </div>
            )}
          </div>
          <div className={styles.productInfo}>
            <h2 className={styles.productName}>{product.title}</h2>
            <ul className={styles.productStats}>
              <li>
                Quantidade: <strong>{product.quantity}</strong>
              </li>
              <li>Bônus: {product.bonus_numbers}</li>
              <li>
                Preço unitário:{' '}
                <strong className={styles.priceHighlight}>
                  {formatCurrency(product.unit_price)}
                </strong>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className={styles.twoCol}>
        <section className={styles.card}>
          <h3 className={styles.sectionTitle}>Resumo do Pedido</h3>
          <div className={styles.summaryRows}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Subtotal</span>
              <span className={styles.summaryValueGreen}>
                {formatCurrency(summary.subtotal)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Valor de Frete</span>
              <span className={styles.summaryValue}>{fmtNum(summary.shipping)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Total Final</span>
              <span className={styles.summaryValueTotal}>
                {formatCurrency(summary.total)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Método de pagamento</span>
              <span className={styles.summaryValue}>
                {summary.payment_method_label}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Hash/Transação</span>
              <span className={`${styles.summaryValue} ${styles.summaryValueHash}`}>
                {summary.transaction_ref}
              </span>
            </div>
          </div>

          <div className={styles.numbersBlock}>
            <p className={styles.numbersTitle}>
              Números Gerados ({lucky_numbers.length})
            </p>
            <div className={styles.numberTags}>
              {lucky_numbers.length === 0 ? (
                <span className={styles.summaryLabel}>Nenhum número ainda.</span>
              ) : (
                lucky_numbers.map((n) => (
                  <span key={n} className={styles.numberTag}>
                    <strong>Número da Sorte:</strong> {n}
                  </span>
                ))
              )}
            </div>
          </div>

          {pix.copy_paste ? (
            <div className={styles.pixBlock}>
              <p className={styles.pixLabel}>PIX</p>
              <div className={styles.pixRow}>
                <input
                  readOnly
                  className={styles.pixInput}
                  value={pix.copy_paste}
                  aria-label="Código PIX copia e cola"
                />
                <button
                  type="button"
                  className={styles.btnCopy}
                  onClick={() => void handleCopyPix()}
                >
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>
          ) : null}

          <button type="button" className={styles.btnPrint} onClick={handlePrint}>
            <Icon icon="mdi:printer" />
            Imprimir
          </button>
        </section>

        <section className={styles.card}>
          <h3 className={styles.sectionTitle}>Entrega &amp; Cliente</h3>
          <ul className={styles.clientList}>
            <li>
              <span className={styles.clientKey}>Cliente:</span> {donorName}
            </li>
            <li>
              <span className={styles.clientKey}>ID Pedido:</span> #{payment.id}
            </li>
            <li>
              <span className={styles.clientKey}>Data:</span>{' '}
              {formatDetailDateTime(paidAt)}
            </li>
            <li>
              <span className={styles.clientKey}>IP:</span>{' '}
              {display(client_context.ip)}
            </li>
            <li>
              <span className={styles.clientKey}>Navegador:</span>{' '}
              {display(client_context.browser ?? client_context.user_agent)}
            </li>
            <li>
              <span className={styles.clientKey}>Plataforma:</span>{' '}
              {display(client_context.platform)}
            </li>
            <li>
              <span className={styles.clientKey}>Dispositivo:</span>{' '}
              {display(client_context.device)}
            </li>
            <li>
              <span className={styles.clientKey}>Localização:</span>{' '}
              {display(client_context.location)}
            </li>
            <li>
              <span className={styles.clientKey}>Provedor:</span>{' '}
              {display(client_context.provider)}
            </li>
          </ul>
          {donor.id != null ? (
            <Link
              to={`/clientes/${donor.id}/editar`}
              className={styles.integrationsLink}
            >
              Integrações
            </Link>
          ) : (
            <span className={styles.integrationsLinkDisabled}>Integrações</span>
          )}
        </section>
      </div>

      {questionnaireAnswers.length > 0 ? (
        <section className={styles.card}>
          <SaleQuestionnaireSection answers={questionnaireAnswers} />
        </section>
      ) : null}

      <section className={`${styles.card} ${styles.trackCard}`}>
        <h3 className={styles.sectionTitleBlue}>
          <Icon icon="mdi:web" style={{ color: '#5e17eb' }} />
          Página da compra
        </h3>
        <ConversionPageLink
          display={tracking.conversion_page_url_display}
          rawUrl={tracking.conversion_page_url}
          showRawWhenDifferent
        />
      </section>

      <section className={`${styles.card} ${styles.trackCard}`}>
        <h3 className={styles.sectionTitleBlue}>
          <Icon icon="mdi:crosshairs-gps" style={{ color: '#ea580c' }} />
          Trackeamento
        </h3>
        <div className={styles.trackSummaryGrid}>
          <div className={styles.trackSummaryItem}>
            <span className={styles.trackSummaryLabel}>Origem</span>
            <strong>{display(tracking.origin)}</strong>
          </div>
          <div className={styles.trackSummaryItem}>
            <span className={styles.trackSummaryLabel}>Campanha</span>
            <strong>{display(tracking.campaign)}</strong>
          </div>
          <div className={styles.trackSummaryItem}>
            <span className={styles.trackSummaryLabel}>Conteúdo</span>
            <strong>{display(tracking.content)}</strong>
          </div>
          <div className={styles.trackSummaryItem}>
            <span className={styles.trackSummaryLabel}>Termo</span>
            <strong>{display(tracking.term)}</strong>
          </div>
          <div className={styles.trackSummaryItem}>
            <span className={styles.trackSummaryLabel}>Canal/AD</span>
            <strong>{display(tracking.ad)}</strong>
          </div>
        </div>

        <div className={styles.trackTouchGrid}>
          <div className={styles.trackTouchCard}>
            <h4 className={styles.trackTouchTitle}>First Touch</h4>
            <ul className={styles.trackList}>
              <li><strong>utm_source:</strong> {renderTouchLabel(firstTouch?.utm_source)}</li>
              <li><strong>utm_medium:</strong> {renderTouchLabel(firstTouch?.utm_medium)}</li>
              <li><strong>utm_campaign:</strong> {renderTouchLabel(firstTouch?.utm_campaign)}</li>
              <li><strong>utm_content:</strong> {renderTouchLabel(firstTouch?.utm_content)}</li>
              <li><strong>utm_term:</strong> {renderTouchLabel(firstTouch?.utm_term)}</li>
              <li><strong>utm_id:</strong> {renderTouchLabel(firstTouch?.utm_id)}</li>
              <li><strong>Capturado em:</strong> {formatDetailDateTime(firstTouch?.captured_at)}</li>
            </ul>
            <p className={styles.trackTinyLabel}>
              Click IDs: {firstTouch?.click_ids ? JSON.stringify(firstTouch.click_ids) : '—'}
            </p>
          </div>

          <div className={styles.trackTouchCard}>
            <h4 className={styles.trackTouchTitle}>Last Touch</h4>
            <ul className={styles.trackList}>
              <li><strong>utm_source:</strong> {renderTouchLabel(lastTouch?.utm_source)}</li>
              <li><strong>utm_medium:</strong> {renderTouchLabel(lastTouch?.utm_medium)}</li>
              <li><strong>utm_campaign:</strong> {renderTouchLabel(lastTouch?.utm_campaign)}</li>
              <li><strong>utm_content:</strong> {renderTouchLabel(lastTouch?.utm_content)}</li>
              <li><strong>utm_term:</strong> {renderTouchLabel(lastTouch?.utm_term)}</li>
              <li><strong>utm_id:</strong> {renderTouchLabel(lastTouch?.utm_id)}</li>
              <li><strong>Capturado em:</strong> {formatDetailDateTime(lastTouch?.captured_at)}</li>
            </ul>
            <p className={styles.trackTinyLabel}>
              Click IDs: {lastTouch?.click_ids ? JSON.stringify(lastTouch.click_ids) : '—'}
            </p>
          </div>
        </div>

        <div className={styles.trackTimelineBlock}>
          <h4 className={styles.trackTouchTitle}>Timeline de Eventos</h4>
          {trackingEvents.length === 0 ? (
            <p className={styles.trackTinyLabel}>Sem eventos detalhados.</p>
          ) : (
            <div className={styles.trackTimelineList}>
              {trackingEvents.map((event, idx) => (
                <div key={`${event.event}-${event.occurred_at ?? idx}`} className={styles.trackTimelineItem}>
                  <div className={styles.trackEventHeader}>
                    <strong>{event.event}</strong>
                    <span>{formatDetailDateTime(event.occurred_at)}</span>
                  </div>
                  <p className={styles.trackTinyLabel}>
                    {[
                      `source=${renderTouchLabel(event.utm_source)}`,
                      `medium=${renderTouchLabel(event.utm_medium)}`,
                      `campaign=${renderTouchLabel(event.utm_campaign)}`,
                      `content=${renderTouchLabel(event.utm_content)}`,
                      `term=${renderTouchLabel(event.utm_term)}`,
                      `id=${renderTouchLabel(event.utm_id)}`,
                    ].join(' | ')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className={styles.card}>
        <h3 className={`${styles.sectionTitle} ${styles.sectionTitleWithIcon}`}>
          <Icon icon="mdi:truck-delivery" className={styles.sectionTitleIcon} aria-hidden />
          Progresso da Entrega
        </h3>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${delivery.progress_percent}%` }}
          />
        </div>
        <div className={styles.progressLabels}>
          <span>{delivery.label_left}</span>
          <span>{delivery.label_right}</span>
        </div>
      </section>

    </div>
  )
}
